/**
 * CaseVendor Enums
 *
 * Closing vendors attached to a case (title companies, escrow, insurance,
 * appraisers, GCs). Issue #1470 — normalised replacement for the
 * `Case.vendors Json?` column shipped in PR #1492 / #1496.
 */

import { z } from 'zod';

// ============================================================================
// VENDOR ROLE
// ============================================================================

/**
 * Role of a vendor on a closing case.
 *
 * Each typed role (everything except OTHER) is unique per case — the database
 * enforces that with a partial unique index `(case_id, vendor_role)` WHERE
 * `vendor_role <> 'OTHER'`. Multiple OTHER rows on the same case are allowed
 * for additional vendors that don't fit the typed slots.
 */
export const VendorRole = {
  TITLE_COMPANY: 'TITLE_COMPANY',
  ESCROW_OFFICER: 'ESCROW_OFFICER',
  INSURANCE_PROVIDER: 'INSURANCE_PROVIDER',
  APPRAISER: 'APPRAISER',
  GC_BUILDER: 'GC_BUILDER',
  OTHER: 'OTHER',
} as const;

export type VendorRole = (typeof VendorRole)[keyof typeof VendorRole];

/** Zod schema for VendorRole validation */
export const vendorRoleSchema = z.nativeEnum(VendorRole);

/** Array of all vendor roles for iteration */
export const VENDOR_ROLES = Object.values(VendorRole);

/**
 * Whether a vendor role is bound by the typed-uniqueness constraint.
 * Returns true for typed roles (TITLE_COMPANY, ESCROW_OFFICER, etc.) and
 * false for OTHER (which allows multiple per case).
 */
export function isTypedVendorRole(role: VendorRole): boolean {
  return role !== VendorRole.OTHER;
}
